import { makeImmutable } from "../core";
import { LocalizationProvider } from "../locale";
import { CartCrossSellingCreator, ContactDetailsLock, DisabledCoupon, EnabledCoupon, FirstOrderDiscount, ProductCardCreator, ProductInstanceCreatorScope, ProductInstanceParser, PurchaseOrderFactory, Quantity, } from "../ordering";
import { InCartPromotionTypes } from "../ordering/InCartPromotionTypes/InCartPromotionTypes";
import { PRestaurantParser } from "../parsing";
import { ConsentsFormController, ContactFormController, FODFormController, FulfillmentFormController, FulfillmentFormReactions, InvoiceFormController, PCart, PCheckout, PCrossSellingCreator, PFulfillmentTime, POrderDetails, PProductCardCreator, PProductInstanceCreator, PPromotionScheme, PPurchaseOrder, PaymentFormController, } from "../presentation";
import { CheckoutInfoService, CheckoutService, GeocodingService, HolidayService, OrderingAvailabilityService, RestaumaticRPCService, ValidatePhoneNumberService, WebGeolocationService, } from "../services";
import { PurchaseOrderStorageManager } from "../storageManagers";
import { ProductCardCrossSellingCreator } from "../ordering/CrossSellingCreator/ProductCardCrossSellingCreator";
export class RestaumaticClient {
    constructor(params) {
        this.purchaseOrderStorageManager = params.purchaseOrderStorageManager;
        this.isQrWaiterOrder = params.isQrWaiterOrder;
        this.validatePhoneService = params.validatePhoneService;
        this.geocodingService = params.geocodingService;
        this.geolocationService = params.geolocationService;
        this.localizationProvider = params.localizationProvider;
        this.checkoutService = params.checkoutService;
        this.checkoutInfoService = params.checkoutInfoService;
        this.holidayService = params.holidayService;
        this.orderingAvailabilityService = params.orderingAvailabilityService;
        this.rpcService = params.rpcService;
        this.menuConsumer = params.menuConsumer;
        makeImmutable(this);
    }
    static create(params) {
        const rpcService = new RestaumaticRPCService({ url: params.rpcUrl });
        const localizationProvider = new LocalizationProvider({
            locale: params.locale,
            fallbackLocale: params.fallbackLocale,
            hrkDualPricesEnabled: params.hrkDualPricesEnabled,
            currency: params.currency,
        });
        const checkoutInfoService = new CheckoutInfoService({
            localizationProvider,
            rpcService,
            isApplePaySupported: params.isApplePaySupported,
        });
        const checkoutService = new CheckoutService({ rpcService });
        const geocodingService = new GeocodingService({
            rpcService,
            siteId: params.siteId,
        });
        const geolocationService = new WebGeolocationService();
        const holidayService = new HolidayService({ rpcService });
        const validatePhoneService = new ValidatePhoneNumberService({
            rpcService,
        });
        const orderingAvailabilityService = new OrderingAvailabilityService({
            rpcService,
        });
        const isQrWaiterOrder = params.menuConsumer === "QrWaiter";
        const purchaseOrderFactory = new PurchaseOrderFactory({
            country: params.country,
            salesChannel: params.salesChannel,
            validatePhoneService,
            geocodingService,
            dineInLocationNumber: params.dineInLocationNumber,
            isQrWaiterOrder,
        });
        const purchaseOrderStorageManager = new PurchaseOrderStorageManager({
            country: params.country,
            isQrWaiterOrder,
            purchaseOrderFactory,
        });
        return new RestaumaticClient({
            purchaseOrderStorageManager,
            isQrWaiterOrder,
            validatePhoneService,
            geocodingService,
            geolocationService,
            localizationProvider,
            checkoutService,
            checkoutInfoService,
            holidayService,
            orderingAvailabilityService,
            rpcService,
            menuConsumer: params.menuConsumer,
        });
    }
    async createPPurchaseOrder(params) {
        var _a;
        const restaurant = await new PRestaurantParser({
            input: params.menuResponse,
            localizationProvider: this.localizationProvider,
            rpcService: this.rpcService,
            checkoutInfoService: this.checkoutInfoService,
            holidayService: this.holidayService,
            orderingAvailabilityService: this.orderingAvailabilityService,
            appOrigin: this.menuConsumer,
        }).parse();
        const inCartPromotionTypes = new InCartPromotionTypes(restaurant.domain.promotionTypes);
        const cartStorageData = this.purchaseOrderStorageManager.restoreCart();
        // NOTICE It is crucial to restore and await cupon before purchaseOrder, because restored promotional items may reference coupon promotion
        const coupon = !restaurant.enableCoupons || this.isQrWaiterOrder
            ? new DisabledCoupon()
            : await EnabledCoupon.fromStorageData({
                promotionService: restaurant.promotionService,
                inCartPromotionTypes,
                couponCode: (_a = cartStorageData === null || cartStorageData === void 0 ? void 0 : cartStorageData.couponCode) !== null && _a !== void 0 ? _a : null,
            });
        const contactDetailsLock = new ContactDetailsLock(coupon);
        const { purchaseOrder, purchaseOrderScope } = this.purchaseOrderStorageManager.restore({
            restaurant: restaurant.domain,
            cartStorageData,
            inCartPromotionTypes,
            contactDetailsLock,
        });
        const pFulfillmentTime = PFulfillmentTime.create({
            purchaseOrderFulfillmentTime: purchaseOrder.purchaseOrderFulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
        });
        const pPromotionScheme = new PPromotionScheme({
            showInactivePromotionInstances: params.showInactivePromotionInstances,
            localizationProvider: this.localizationProvider,
            restaurant,
            domain: purchaseOrder.promotionScheme,
            coupon,
        });
        const pOrderDetails = new POrderDetails({
            restaurant,
            orderReceiver: purchaseOrder.orderReceiver,
            geocodingService: this.geocodingService,
            fulfillmentTime: pFulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            promotionScheme: pPromotionScheme,
        });
        const fulfillmentFormController = FulfillmentFormController.create({
            purchaseOrderFulfillmentTime: purchaseOrder.purchaseOrderFulfillmentTime,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
            fulfillmentTime: pFulfillmentTime,
            geocodingService: this.geocodingService,
            geolocationService: this.geolocationService,
            viewType: params.viewType,
        });
        const fulfillmentFormReactions = new FulfillmentFormReactions({
            fulfillmentTime: pOrderDetails.fulfillmentTime,
            fulfillmentFormController,
        });
        const pPurchaseOrder = new PPurchaseOrder({
            domain: purchaseOrder,
            restaurant,
            localizationProvider: this.localizationProvider,
        });
        return {
            restaurant,
            purchaseOrder,
            purchaseOrderScope,
            fulfillmentFormController,
            fulfillmentFormReactions,
            pPurchaseOrder,
            pOrderDetails,
        };
    }
    async createCheckout(menuResponse, placeOrderCallback) {
        const { restaurant, purchaseOrder, fulfillmentFormController, fulfillmentFormReactions, pPurchaseOrder, pOrderDetails, } = await this.createPPurchaseOrder({
            menuResponse,
            showInactivePromotionInstances: false,
            viewType: "Checkout",
        });
        const consentsFormController = new ConsentsFormController({
            consents: purchaseOrder.consents,
        });
        const invoiceFormController = new InvoiceFormController({
            orderReceiver: purchaseOrder.orderReceiver,
        });
        const paymentFormController = new PaymentFormController({
            paymentMediums: restaurant.paymentMediums,
            fulfillmentInstructions: purchaseOrder.fulfillmentInstructions,
        });
        const contactFormController = ContactFormController.fromContactDetails(purchaseOrder.contactDetails);
        const checkout = new PCheckout({
            purchaseOrder: pPurchaseOrder,
            restaurant,
            checkoutService: this.checkoutService,
            localizationProvider: this.localizationProvider,
            orderDetails: pOrderDetails,
            fulfillmentFormController,
            paymentFormController,
            contactFormController,
            consentsFormController,
            invoiceFormController,
            fulfillmentFormReactions,
            locale: this.localizationProvider.locale,
            placeOrderCallback,
        });
        return checkout;
    }
    async createCart(menuResponse) {
        const { restaurant, purchaseOrder, purchaseOrderScope, fulfillmentFormController, fulfillmentFormReactions, pPurchaseOrder, pOrderDetails, } = await this.createPPurchaseOrder({
            menuResponse,
            showInactivePromotionInstances: true,
            viewType: "Cart",
        });
        const firstOrderDiscount = FirstOrderDiscount.create({
            restaurantId: restaurant.id,
            validatePhoneService: this.validatePhoneService,
            checkoutService: this.checkoutService,
            contactDetails: purchaseOrder.contactDetails,
            restaurantEnableFod: restaurant.enableFod,
            isQrWaiterOrder: this.isQrWaiterOrder,
        });
        const fodFormController = FODFormController.create(firstOrderDiscount);
        const domainCrossSellingCreator = CartCrossSellingCreator.create({
            listProvider: restaurant.domain.crossSellingListProvider,
            menu: restaurant.domain.menu,
            purchaseOrderPayload: purchaseOrder.payload,
            purchaseOrderScope,
        });
        const cart = new PCart({
            purchaseOrder: pPurchaseOrder,
            restaurant,
            orderDetails: pOrderDetails,
            firstOrderDiscount,
            fodFormController,
            fulfillmentFormController,
            fulfillmentFormReactions,
            crossSellingCreator: new PCrossSellingCreator({
                domain: domainCrossSellingCreator,
                localizationProvider: this.localizationProvider,
                restaurant,
            }),
        });
        return {
            cart,
            restaurant,
            purchaseOrderScope,
            purchaseOrderPayload: purchaseOrder.payload,
        };
    }
    createCreator(params) {
        const menu = params.restaurant.domain.menu;
        const productInstanceParser = new ProductInstanceParser({
            ...menu,
        });
        const domainProductInstanceCreator = productInstanceParser.parseCreator(params.cartProductKey);
        if (domainProductInstanceCreator === null) {
            return null;
        }
        const domainCrossSellingCreator = ProductCardCrossSellingCreator.create({
            listProvider: params.restaurant.domain.crossSellingListProvider,
            menu,
            purchaseOrderPayload: params.purchaseOrderPayload,
        });
        const domainProductCardCreator = new ProductCardCreator({
            productInstanceCreator: domainProductInstanceCreator,
            quantity: Quantity.One,
            crossSellingCreator: domainCrossSellingCreator,
        });
        const productInstanceCreatorScope = new ProductInstanceCreatorScope(params.purchaseOrderScope);
        domainProductCardCreator.initWithScope(productInstanceCreatorScope);
        return new PProductCardCreator({
            localizationProvider: this.localizationProvider,
            productTypes: menu.productTypes,
            domain: domainProductCardCreator,
            productInstanceCreator: PProductInstanceCreator.create({
                domain: domainProductInstanceCreator,
                localizationProvider: this.localizationProvider,
                restaurant: params.restaurant,
            }),
            crossSellingCreator: new PCrossSellingCreator({
                domain: domainCrossSellingCreator,
                localizationProvider: this.localizationProvider,
                restaurant: params.restaurant,
            }),
        });
    }
}
